// react
import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AxiosError, AxiosInstance } from 'axios';

// components
import { OnboardingHeader } from '../../components/OnboardingHeader';
import { PageHeadline } from '../../components/atoms';
import { AccountService } from '../../services/AccountService';
import { useApi } from '../../services/hooks/apiHook';
import { useAuth } from '../../store/auth';
import { useTranslation } from 'react-i18next';
import { errorToast } from '../../utils/toasts';
import { errorMessageAdapter } from '../../adapters';
import { isPaymentMade } from '../../services/utils/mappingStatus';
import { PaymentStatus } from '../../services/Interfaces/IAccountService';
import { randomInt } from '../../utils/random';

/**
 * AwaitingApproval component for displaying a payment confirmation waiting screen.
 *
 * @component
 * @returns The JSX element representing the AwaitingApproval component.
 */
const AwaitingApproval: FC = () => {
  // null => not paid, false => paid but not approved, true => paid and approved
  const [isCheckoutPaid, setIsCheckoutPaid] = useState<null | boolean>(null);
  const { token, planSlug, setPlanSlug } = useAuth();
  const { i18n, t } = useTranslation();
  const randomImage = useMemo(() => randomInt(1, 5), []);

  const api: AxiosInstance = useApi();

  const navigate = useNavigate();

  useEffect(() => {
    const ensurePlanSlug = async () => {
      if (!token || planSlug) {
        return;
      }

      try {
        const { account_plan_slug } = await AccountService.getAccountPlans(api, {
          headers: { 'Content-Language': i18n.language, Authorization: `Bearer ${token}` }
        });
        if (account_plan_slug) {
          setPlanSlug(account_plan_slug);
        }
      } catch (e) {
        errorToast(errorMessageAdapter(e as AxiosError));
      }
    };

    void ensurePlanSlug();
  }, [api, i18n.language, planSlug, setPlanSlug, token]);

  useEffect(() => {
    if (!token || !planSlug) {
      return;
    }

    const fetchCheckoutStatus = async () => {
      try {
        const { data } = await AccountService.getCheckoutStatus(
          api,
          { slug_plan: planSlug },
          {
            headers: { 'Content-Language': i18n.language, Authorization: `Bearer ${token}` }
          }
        );

        if (!data?.status) {
          return;
        }

        setIsCheckoutPaid(isPaymentMade(data?.status || PaymentStatus.Pending));
      } catch (e) {
        errorToast(errorMessageAdapter(e as AxiosError));
      }
    };

    void fetchCheckoutStatus();
    const timerId = setInterval(() => fetchCheckoutStatus(), 5000);

    return () => {
      clearInterval(timerId);
    };
  }, [api, i18n.language, planSlug, token]);

  useEffect(() => {
    if (isCheckoutPaid === null) {
      navigate('/payment-awaiting-approval');
      return;
    }

    if (isCheckoutPaid) {
      navigate('/payment-succeeded');
      return;
    }

    navigate('/payment-failed');
  }, [isCheckoutPaid]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 lg:flex">
      <div className="w-full lg:w-4/5">
        <div className="mx-auto max-w-7xl px-6 py-8 lg:px-12">
          <OnboardingHeader />

          <div className="mt-6">
            <PageHeadline
              title={t('PaymentProcessingTitle')}
              subtitle={t('PaymentProcessingDescription')}
              className="mt-8 shadow-xl"
              gradientFrom="from-primary"
              gradientTo="to-blue-800"
            />
          </div>

          <div className="mt-8 flex justify-center">
            <div className="w-full max-w-md rounded-xl bg-white p-8 shadow-sm ring-1 ring-slate-900/5">
              <p className="text-sm text-slate-600">{t('PaymentProcessingDescription')}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="hidden lg:block lg:w-1/5">
        <div className="relative h-screen">
          <img src={`/img/image0${randomImage}.png`} className="h-full w-full object-cover" alt="" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent" />
          <p className="absolute bottom-[25%] left-6 right-6 text-[32px] font-bold leading-tight text-white">
            {t('labels.lblWeveHelped')}
          </p>
        </div>
      </div>
    </div>
  );
};

export { AwaitingApproval };
